import { BreakRuleForm } from 'components/dialog/break_rule/BreakRuleForm';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { useTranslation } from 'react-i18next';
import { BreakRule } from 'repository/timekeeper/@types/BreakRuleModel';
import { useRestParametersForm } from 'hooks/restParameters/useRestParametersForm';
import { FormDelete } from '../paycode/FormDelete';

type Props = {
    open: boolean;
    setOpen: (value: boolean) => void;
    onClose: () => void;
    rows: BreakRule[];
    selectedRow: BreakRule[];
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
};

export const BreakRuleDialog = ({ open, onClose, rows, selectedRow, reloading, mode, setOpen }: Props) => {
    const { t } = useTranslation();
    const form = useRestParametersForm({ rows, onClose, reloading, mode, selectedRow, open, setOpen });

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                if (mode === 'delete' || mode === 'info') {
                    onClose();
                } else {
                    form.requestClose();
                }
            }}
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {mode === 'add' && t('settings.break_rules.dialog.titles.add')}
                {mode === 'edit' && t('settings.break_rules.dialog.titles.edit')}
                {mode === 'delete' && t('settings.break_rules.dialog.titles.delete')}
                {mode === 'info' && t('settings.break_rules.dialog.titles.info')}
                {mode === 'duplicate' && t('settings.break_rules.dialog.titles.duplicate')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => {
                    if (mode === 'delete' || mode === 'info') {
                        form.closeForm();
                    } else {
                        form.requestClose();
                    }
                }}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>{form.alert && <AlertCustom alerts={form.alert} setAlerts={form.setAlert} closeForm={form.closeForm} t={t} />}</Box>
            <DialogContent dividers>
                {mode === 'delete' ? (
                    <FormDelete
                        open={open}
                        title={t('settings.break_rules.dialog.titles.delete')}
                        description={t('settings.break_rules.dialog.alerts.delete.message')}
                        names={selectedRow.map((row) => row.name)}
                        onCancel={onClose}
                        onConfirm={form.submit}
                        textButton={t('general.buttons.delete')}
                        mode="delete"
                    />
                ) : (
                    <BreakRuleForm form={form} mode={mode} />
                )}
            </DialogContent>
            {mode === 'info' && (
                <DialogActions>
                    <Button variant="contained" disableElevation onClick={() => form.closeForm()}>
                        {t('general.buttons.close')}
                    </Button>
                </DialogActions>
            )}
            {mode !== 'delete' && mode !== 'info' && (
                <DialogActions sx={{ pb: 2, pr: 3 }}>
                    <Button variant="contained" disableElevation color="error" onClick={() => form.requestClose()}>
                        {t('general.buttons.cancel')}
                    </Button>
                    <Button variant="contained" disableElevation onClick={() => form.submit()} disabled={form.isSubmitDisabled} loading={form.loading}>
                        {t('general.buttons.save')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
